import {
  Box,
  Text,
  Checkbox,
  Radio,
  VStack,
  HStack,
  Button,
  Input,
  Textarea,
  useToast,
  IconButton,
  Flex,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Accordion,
  FormLabel,
} from '@chakra-ui/react';
import { DeleteIcon } from '@chakra-ui/icons';
import { useState, useEffect } from 'react';
import { getData } from 'utils/httpClient';
import VehicleHistoryModalComplete from '../Vehicles/VehicleHistoryModalComplete';
import SchedulerServiceHero from './SchedulerServiceHero';
import SchedulerCollapsibleSection from './SchedulerCollapsibleSection';
import { secondaryButtonProps } from '../../theme/adminUi';
import ServiceProductivityPanel from './ServiceProductivityPanel';
import ServiceDynamicTestPanel from './ServiceDynamicTestPanel';
import ProductSearchSelect from '../stock/ProductSearchSelect';
import authService from 'services/authService';
import {
  isSimplifiedWorkshopView,
  isWarrantyServiceType,
} from '../../utils/warrantyTypes';

const SchedulerModalForm = ({
  selectedEvent,
  vehicleService,
  completedEvents,
  setCompletedEvents,
  selectedRecommendedProducts,
  setSelectedRecommendedProducts,
  additionalProducts,
  setAdditionalProducts,
  observations,
  setObservations,
  serviceObservations,
  setServiceObservations,
  productsList,
  mechanicsList,
  assistant,
  setAssistant,
  onMechanicChange,
  customerObservations,
  setCustomerObservations,
  hasAlerts,
  onOpenAlerts,
  productivityTimer,
  assistantProductivityTimer,
  assistantName,
  dynamicTestStart,
  dynamicTestEnd,
  dynamicTestNotApplicable,
  onStartDynamicTest,
  onEndDynamicTest,
  onDynamicTestNotApplicableChange,
  isSavingDynamicTest,
}) => {
  const [showSearch, setShowSearch] = useState(false);
  const [pickProductId, setPickProductId] = useState('');
  const [pickedProduct, setPickedProduct] = useState(null);
  const [stockInfo, setStockInfo] = useState(null);
  const toast = useToast();
  const {
    isOpen: isVehicleHistoryModalOpen,
    onOpen: onVehicleHistoryModalOpen,
    onClose: onVehicleHistoryModalClose,
  } = useDisclosure();
  const [vehicleData, setVehicleData] = useState(null);
  const [loadingVehicle, setLoadingVehicle] = useState(false);
  const appointment = selectedEvent?.appointment;

  const fetchVehicleData = async (vehicleId) => {
    try {
      setLoadingVehicle(true);
      const response = await getData(`/vehicles/${vehicleId}`, { skipLoader: true });
      setVehicleData(response?.payload?.vehicle || null);
      onVehicleHistoryModalOpen();
    } catch {
      toast({
        title: 'Error al obtener datos del vehículo',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingVehicle(false);
    }
  };

  const handleEventCommentChange = (eventId, comment) => {
    setCompletedEvents((prev) => ({
      ...prev,
      [eventId]: { ...(prev[eventId] || {}), comment },
    }));
  };

  const handleEventStatusChange = (eventId, status) => {
    const normalized = status || null;
    setCompletedEvents((prev) => ({
      ...prev,
      [eventId]: {
        ...(prev[eventId] || {}),
        status: normalized,
        completed: normalized === 'Ok',
      },
    }));
  };

  const handleEventReparadoChange = (eventId, reparado) => {
    setCompletedEvents((prev) => ({
      ...prev,
      [eventId]: { ...(prev[eventId] || {}), reparado: !!reparado },
    }));
  };

  const handleRecommendedProductChange = (product, isChecked) => {
    if (isChecked) {
      setSelectedRecommendedProducts((prev) => [
        ...prev,
        { id: product.productId, name: product.product.name },
      ]);
    } else {
      setSelectedRecommendedProducts((prev) =>
        prev.filter((item) => item.id !== product.productId),
      );
    }
  };

  const loadStockInfo = async (productId) => {
    if (!productId) {
      setStockInfo(null);
      return;
    }
    try {
      const qs = vehicleService?.id ? `?vehicleServiceId=${vehicleService.id}` : '';
      const res = await getData(`/stock/availability/${productId}${qs}`, {
        skipLoader: true,
      });
      setStockInfo(res?.payload || null);
    } catch {
      setStockInfo(null);
    }
  };

  const handleSelectProduct = (productId, product) => {
    if (!productId || !product) return;
    if (additionalProducts.some((p) => p.id === product.id)) {
      toast({
        title: 'El producto ya está en la lista',
        status: 'info',
        duration: 2000,
      });
      return;
    }
    setAdditionalProducts((prev) => [...prev, { ...product, quantity: 1 }]);
    setPickProductId('');
    setShowSearch(false);
    setStockInfo(null);
  };

  useEffect(() => {
    if (pickProductId) loadStockInfo(pickProductId);
    else setStockInfo(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickProductId]);

  const updateProductQuantity = (index, value) => {
    setAdditionalProducts((prev) =>
      prev.map((product, i) => (i === index ? { ...product, quantity: value } : product)),
    );
  };

  const handleDeleteProduct = (index) => {
    setAdditionalProducts((prev) => prev.filter((_, i) => i !== index));
  };

  const serviceTypeRef =
    appointment?.serviceType ||
    appointment?.serviceTypeId ||
    vehicleService?.serviceType ||
    vehicleService?.serviceTypeId;
  const isWarrantyService = isWarrantyServiceType(serviceTypeRef);
  const isSimplifiedView = isSimplifiedWorkshopView(serviceTypeRef, [], {
    warrantyType: vehicleService?.warrantyType || appointment?.warrantyType,
    description:
      vehicleService?.description ||
      appointment?.description ||
      appointment?.vehicleService?.description,
    serviceName:
      vehicleService?.serviceName || appointment?.vehicleService?.serviceName,
    agenda: vehicleService?.agenda || appointment,
    service: vehicleService?.service || appointment?.service,
  });

  const checklistCount = vehicleService?.vehicleServiceEventsHistories?.length || 0;
  const sortedEventHistories = [
    ...(vehicleService?.vehicleServiceEventsHistories || []),
  ].sort((a, b) =>
    String(a?.event?.code ?? '').localeCompare(String(b?.event?.code ?? ''), 'es', {
      numeric: true,
      sensitivity: 'base',
    }),
  );

  const productivityProps = productivityTimer
    ? {
        title: 'PRODUCTIVIDAD MECÁNICO',
        elapsedSeconds: productivityTimer.elapsedSeconds,
        isRunning: productivityTimer.isRunning,
        isPaused: productivityTimer.isPaused,
        isFinalized: productivityTimer.isFinalized,
        isActive: productivityTimer.isActive,
        canControl: productivityTimer.canControl,
        onPause: productivityTimer.pause,
        onResume: productivityTimer.resume,
      }
    : {};

  const assistantProductivityProps =
    assistantProductivityTimer?.enabled
      ? {
          title: 'PRODUCTIVIDAD AYUDANTE',
          subtitle: assistantName || null,
          elapsedSeconds: assistantProductivityTimer.elapsedSeconds,
          isRunning: assistantProductivityTimer.isRunning,
          isPaused: assistantProductivityTimer.isPaused,
          isFinalized: assistantProductivityTimer.isFinalized,
          isActive: assistantProductivityTimer.isActive,
          canControl: assistantProductivityTimer.canControl,
          onPause: assistantProductivityTimer.pause,
          onResume: assistantProductivityTimer.resume,
        }
      : null;

  return (
    <Box w="100%">
      <Flex gap={4} align="flex-start" w="100%" mb={4} flexDirection={{ base: 'column', lg: 'row' }}>
        <Box flex={1} minW={0}>
          <SchedulerServiceHero
            appointment={appointment}
            vehicleService={vehicleService}
            start={selectedEvent?.start}
            end={selectedEvent?.end}
            onOpenHistory={() => fetchVehicleData(appointment?.vehicle?.id)}
            onOpenAlerts={onOpenAlerts}
            hasAlerts={hasAlerts}
            loadingHistory={loadingVehicle}
            assistant={assistant}
            setAssistant={setAssistant}
            mechanicsList={mechanicsList}
            mechanicId={appointment?.mechanicId || vehicleService?.mechanicId}
            onMechanicChange={onMechanicChange}
            showStaffEditor={authService.isAdmin()}
            assistantName={assistantName || vehicleService?.assistant?.name}
          />
        </Box>

        <Box
          w={{ base: '100%', lg: '280px' }}
          flexShrink={0}
        >
          <VStack spacing={3} align="stretch">
            <ServiceProductivityPanel {...productivityProps} />
            {assistantProductivityProps ? (
              <ServiceProductivityPanel {...assistantProductivityProps} />
            ) : null}
            <ServiceDynamicTestPanel
              start={dynamicTestStart}
              end={dynamicTestEnd}
              notApplicable={dynamicTestNotApplicable}
              canControl
              onStart={onStartDynamicTest}
              onEnd={onEndDynamicTest}
              onNotApplicableChange={onDynamicTestNotApplicableChange}
              isSaving={isSavingDynamicTest}
            />
          </VStack>
        </Box>
      </Flex>

      <Box mb={4}>
        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
          Petición del cliente
        </Text>
        <Textarea
          value={customerObservations}
          onChange={(e) => setCustomerObservations(e.target.value)}
          placeholder="Motivo de visita…"
          size="sm"
          minH="64px"
          bg="white"
        />
      </Box>

      <Accordion
        allowMultiple
        defaultIndex={isSimplifiedView ? [0] : [0, 1, 2]}
        reduceMotion
      >
          {!isSimplifiedView && (
          <SchedulerCollapsibleSection title="Productos utilizados">
            <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
              Productos recomendados
            </Text>
            <VStack align="stretch" spacing={2} mb={4}>
              {vehicleService?.service?.recommendedProducts?.length > 0 ? (
                vehicleService.service.recommendedProducts.map((rp) => (
                  <HStack key={rp.productId} spacing={3} align="flex-start">
                    <Checkbox
                      isChecked={selectedRecommendedProducts.some(
                        (p) => p.id === rp.productId,
                      )}
                      onChange={(e) =>
                        handleRecommendedProductChange(rp, e.target.checked)
                      }
                    />
                    <Box>
                      <Text fontSize="sm" fontWeight="medium">
                        {rp.product?.name || 'Producto'}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Cant. {rp.quantity}
                        {rp.product?.code ? ` · ${rp.product.code}` : ''}
                      </Text>
                    </Box>
                  </HStack>
                ))
              ) : null}
            </VStack>

            <Button
              {...secondaryButtonProps}
              mb={2}
              onClick={() => setShowSearch(true)}
            >
              + Agregar producto
            </Button>

            {showSearch && (
              <Box mb={3}>
                <ProductSearchSelect
                  value={pickProductId}
                  onChange={(id, product) => {
                    setPickProductId(id || '');
                    setPickedProduct(product || null);
                  }}
                  placeholder="Buscar por código o nombre del producto…"
                />
                <Button
                  size="sm"
                  mt={2}
                  colorScheme="gray"
                  isDisabled={!pickedProduct}
                  onClick={() => handleSelectProduct(pickProductId, pickedProduct)}
                >
                  Agregar a la lista
                </Button>
                {stockInfo && pickProductId && (
                  <Text mt={2} fontSize="xs" fontWeight="semibold">
                    {stockInfo.stockTracked === false
                      ? 'Categoría Servicio: no sujeto a stock'
                      : `Disponible: ${
                          stockInfo.effectiveAvailable ??
                          stockInfo.totalAvailable ??
                          0
                        }`}
                  </Text>
                )}
              </Box>
            )}

            {additionalProducts.length > 0 && (
              <VStack align="stretch" spacing={2}>
                {additionalProducts.map((product, index) => (
                  <HStack
                    key={index}
                    p={2}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="gray.50"
                    spacing={2}
                  >
                    <Box flex={1}>
                      <Text fontSize="sm" fontWeight="medium">
                        {product.name}
                      </Text>
                      {product.code && (
                        <Text fontSize="xs" color="gray.500">
                          {product.code}
                        </Text>
                      )}
                    </Box>
                    <Input
                      type="number"
                      size="xs"
                      w="70px"
                      min={1}
                      value={product.quantity}
                      onChange={(e) => updateProductQuantity(index, e.target.value)}
                    />
                    <IconButton
                      size="xs"
                      icon={<DeleteIcon />}
                      colorScheme="red"
                      variant="ghost"
                      aria-label="Quitar"
                      onClick={() => handleDeleteProduct(index)}
                    />
                  </HStack>
                ))}
              </VStack>
            )}
          </SchedulerCollapsibleSection>
          )}

          {!isSimplifiedView && (
          <SchedulerCollapsibleSection
            title="Checklist de tareas"
          >
            {checklistCount > 0 ? (
              <TableContainer maxH="40vh" overflowY="auto">
                <Table variant="simple" size="sm">
                  <Thead bg="gray.50" position="sticky" top={0} zIndex={1}>
                    <Tr>
                      <Th w="60px">Cód.</Th>
                      <Th>Descripción</Th>
                      <Th w="160px">Estado</Th>
                      <Th w="90px">Reparado</Th>
                      <Th>Nota</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {sortedEventHistories.map((serviceEvent) => {
                      const eventState = completedEvents[serviceEvent.id] || {};
                      const statusValue =
                        eventState.status === 'Ok' || eventState.status === 'NoOk'
                          ? eventState.status
                          : eventState.completed
                            ? 'Ok'
                            : '';
                      const isOk = statusValue === 'Ok';
                      const isNoOk = statusValue === 'NoOk';
                      const isReparado = !!eventState.reparado;

                      return (
                        <Tr
                          key={serviceEvent.id}
                          _hover={{ bg: 'gray.50' }}
                          bg={
                            isOk
                              ? 'green.50'
                              : isNoOk
                                ? 'red.50'
                                : undefined
                          }
                        >
                          <Td fontSize="xs" fontWeight="bold" color="gray.500">
                            {serviceEvent.event?.code || '—'}
                          </Td>
                          <Td
                            fontSize="sm"
                            fontWeight="medium"
                            color={isOk ? 'gray.600' : 'gray.800'}
                          >
                            {serviceEvent.eventName ||
                              serviceEvent.event?.name ||
                              '—'}
                          </Td>
                          <Td>
                            <HStack spacing={3}>
                              <Radio
                                size="sm"
                                colorScheme="green"
                                isChecked={statusValue === 'Ok'}
                                onClick={() =>
                                  handleEventStatusChange(
                                    serviceEvent.id,
                                    statusValue === 'Ok' ? null : 'Ok',
                                  )
                                }
                              >
                                OK
                              </Radio>
                              <Radio
                                size="sm"
                                colorScheme="red"
                                isChecked={statusValue === 'NoOk'}
                                onClick={() =>
                                  handleEventStatusChange(
                                    serviceEvent.id,
                                    statusValue === 'NoOk' ? null : 'NoOk',
                                  )
                                }
                              >
                                nOk
                              </Radio>
                            </HStack>
                          </Td>
                          <Td>
                            <Checkbox
                              size="sm"
                              colorScheme="blue"
                              isChecked={isReparado}
                              onChange={(e) =>
                                handleEventReparadoChange(
                                  serviceEvent.id,
                                  e.target.checked,
                                )
                              }
                            >
                              Sí
                            </Checkbox>
                          </Td>
                          <Td>
                            {serviceEvent.event?.hasComment ? (
                              <Input
                                size="xs"
                                value={(() => {
                                  const comment = eventState.comment;
                                  if (
                                    !comment ||
                                    comment === 0 ||
                                    String(comment).trim() === '0'
                                  ) {
                                    return '';
                                  }
                                  return String(comment);
                                })()}
                                onChange={(e) =>
                                  handleEventCommentChange(
                                    serviceEvent.id,
                                    e.target.value,
                                  )
                                }
                                placeholder="Nota…"
                                bg="white"
                              />
                            ) : null}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : null}
          </SchedulerCollapsibleSection>
          )}

          <SchedulerCollapsibleSection
            title={
              isWarrantyService
                ? 'Observaciones finales'
                : isSimplifiedView
                  ? 'Resumen técnico'
                  : 'Observaciones'
            }
          >
            <FormLabel fontSize="xs">
              {isWarrantyService
                ? 'Observaciones finales'
                : isSimplifiedView
                  ? 'Observaciones / resumen técnico'
                  : 'Observaciones del técnico'}
            </FormLabel>
            <Textarea
              size="sm"
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              placeholder="Notas durante el trabajo…"
              minH="80px"
              mb={3}
            />
            {appointment?.status === 'Completado' && (
              <>
                <FormLabel fontSize="xs">Observaciones del servicio</FormLabel>
                <Textarea
                  size="sm"
                  value={serviceObservations}
                  onChange={(e) => setServiceObservations(e.target.value)}
                  placeholder="Resumen al cerrar el servicio…"
                  minH="80px"
                />
              </>
            )}
          </SchedulerCollapsibleSection>
        </Accordion>

      <VehicleHistoryModalComplete
        isOpen={isVehicleHistoryModalOpen}
        onClose={onVehicleHistoryModalClose}
        vehicleData={vehicleData}
        isLoading={loadingVehicle}
      />
    </Box>
  );
};

export default SchedulerModalForm;
